import { BtnPrimary } from '@components/buttons';
import { AsyncForm, FormGroup } from '@components/async-form';
import {
  IcoEye,
  IcoGlobe,
  IcoHeart,
  IcoLightning,
  IcoLock,
  IcoMail,
  IcoPencil,
} from '@components/icons';
import { useAuth, useCurrentUser } from 'client/lib/auth';
import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { autoFocusSelf } from 'client/utils/autofocus';
import { randomString } from 'shared/auth';
import { detectTimezone } from 'shared/dateutil';

export function WelcomeEmailModal(props: { userName: string }) {
  const user = useCurrentUser();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendProvisioningLink = async (email: string) => {
    await rpx.auth.calendarForgotPassword({ email, mode: 'modal' });
    setSent(true);
    setIsSubmitting(false);
  };

  const onSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    if (user) {
      return sendProvisioningLink(user.email);
    }
    try {
      const { exists } = await rpx.auth.isEmailTaken({ email });
      if (!exists) {
        // New user...
        const user = await rpx.auth.registerNewUser({
          email,
          name: props.userName || email.split('@')[0],
          password: randomString(32, 39),
          timezone: detectTimezone(),
        });
        auth.setUser(user);
      }
      await sendProvisioningLink(email);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div class="fixed inset-0 bg-black/50 z-40" />
      <section class="z-50 p-10 text-base w-xl flex flex-col gap-6 overflow-auto leading-normal fixed top-4 right-4 bottom-4 bg-white/95 backdrop-blur-2xl rounded-2xl an-fade-in-left shadow-2xl">
        <header class="flex items-center text-center flex-col gap-4">
          <div class="inline-flex items-center justify-center size-16 rounded-full bg-gradient-to-br from-sky-600 to bg-pink-400 via-violet-400 text-2xl relative">
            🚀
            <span class="absolute shadow-lg shadow-indigo-700 animate-ping-slow inset-0 rounded-full"></span>
          </div>

          <h1 class="capitalize text-3xl font-bold">Congrats! You just skipped the hard part...</h1>

          <p class="text-gray-600 text-lg leading-normal">
            You went from idea to a professional, personalized page. Meet your new launchpad: a
            place to share your expertise, connect with clients, and grow your business.
          </p>
        </header>
        {sent && (
          <section class="bg-green-50 rounded-xl p-6 flex flex-col gap-2 text-green-700">
            <div class="flex gap-4">
              <span class="flex items-center justify-center size-12 rounded-full bg-green-100 text-green-600 shrink-0">
                <IcoMail class="size-6" />
              </span>
              <div>
                <h3 class="font-bold text-green-600">Check your inbox for a confirmation link</h3>
                We emailed a link to <strong>{email}</strong> - click the link to get your custom
                page!
              </div>
            </div>
          </section>
        )}
        {!sent && (
          <AsyncForm onSubmit={onSubmit} class="bg-gray-50 rounded-xl p-6 flex flex-col gap-4">
            <header class="flex items-center text-center flex-col gap-2">
              <h2 class="capitalize text-xl font-bold">Save your personalized page</h2>
              <p class="text-gray-600 text-base leading-normal">
                We'll email you the direct link so you can keep access to your this page and edit it
                any time you want.
              </p>
            </header>
            {!user && (
              <section class="flex gap-2 items-start">
                <FormGroup prop="email" class="grow">
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IcoMail class="size-6 opacity-75" />
                    </span>
                    <input
                      type="email"
                      defaultValue={email || ''}
                      ref={autoFocusSelf}
                      onInput={(e: any) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      class="ruz-input text-base pl-12"
                    />
                  </div>
                </FormGroup>
                <BtnPrimary class="capitalize shrink-0" isLoading={isSubmitting}>
                  Save my page
                </BtnPrimary>
              </section>
            )}
            {user && (
              <BtnPrimary class="capitalize" isLoading={isSubmitting}>
                Save my page
              </BtnPrimary>
            )}
            <footer>
              <ul class="flex justify-around text-center text-sm text-gray-500">
                <li class="flex items-center gap-2">
                  <IcoLock /> No spam, ever
                </li>
                <li class="flex items-center gap-2">
                  <IcoLightning /> Instant access
                </li>
                <li class="flex items-center gap-2">
                  <IcoHeart /> Free support
                </li>
              </ul>
            </footer>
          </AsyncForm>
        )}
        <footer>
          <ul class="text-gray-600 flex flex-col gap-6">
            <li class="flex gap-4">
              <span class="flex items-center justify-center size-12 rounded-full bg-violet-100 text-violet-600 shrink-0">
                <IcoEye class="size-6" />
              </span>
              <div>
                <h3 class="font-bold">Explore your page</h3> See how we captured your unique
                approach and expertise
              </div>
            </li>
            <li class="flex gap-4">
              <span class="flex items-center justify-center size-12 rounded-full bg-violet-100 text-violet-600 shrink-0">
                <IcoPencil class="size-6" />
              </span>
              <div>
                <h3 class="font-bold">Customize anything</h3> Edit text, adjust messaging, and
                perfect your brand
              </div>
            </li>
            <li class="flex gap-4 sh">
              <span class="flex items-center justify-center size-12 rounded-full bg-violet-100 text-violet-600 shrink-0">
                <IcoGlobe class="size-6" />
              </span>
              <div>
                <h3 class="font-bold">Publish instantly</h3> Go live and start booking calls the
                moment you're ready
              </div>
            </li>
          </ul>
        </footer>
      </section>
    </>
  );
}
